import {
  BadgeAdapter,
  getDisclaimerViewModel,
  getGalleryModel,
  getHotelFlags,
  getPriceModel,
  getPrimaryCardMessages,
  getRecentlyViewed,
  getReviewsModel,
  getShortList,
  mapPropertySearchResults,
  SearchMigrationAdapter
} from 'src/common/stores/adapters/bex-api';

import {
  DisclaimerViewModel,
  GalleryViewModel,
  HotelFlags,
  HotelResultsViewModel,
  HotelViewModel,
  PriceViewModel,
  PrimaryCardMessagingModel,
  PropertyListingBadgeModel,
  RecentlyViewedModel,
  ShortlistModel
} from 'models';

import { OperationResult } from '__generated__/api/api-operations';
import { PropertyMapMigration } from '__generated__/api';

export const adaptProperties: SearchMigrationAdapter = (
  bffResults: HotelResultsViewModel,
  bexApiResults: OperationResult<PropertyMapMigration.Query>
): HotelResultsViewModel => {
  const { data } = bexApiResults;
  const { propertySearch } = data;

  if (!propertySearch || !propertySearch.properties) {
    return bffResults;
  }

  return {
    ...bffResults,
    hotels: mapPropertySearchResults(data)
  };
};

export const adaptReviews = (property: PropertyMapMigration.Properties, hotel: HotelViewModel): HotelViewModel => ({
  ...hotel,
  reviews: property && property.reviews ? getReviewsModel(property.reviews) : hotel.reviews
});

// Wave 2 is the property gallery
export const adaptPropertiesWave2: SearchMigrationAdapter = (
  bffResults: HotelResultsViewModel,
  bexApiResults: OperationResult<PropertyMapMigration.Query>,
  bexApiPropertyDict: { [key: string]: PropertyMapMigration.Properties }
): HotelResultsViewModel => adaptPropertyField(bffResults, bexApiResults, getGallery, bexApiPropertyDict);

const getGallery = (property: PropertyMapMigration.Properties, bffHotel: HotelViewModel): { gallery: GalleryViewModel } => ({
  gallery: property && property.propertyGallery ? getGalleryModel(property.propertyGallery) : bffHotel.gallery
});

// Wave 3 is the property price
export const adaptPropertiesWave3: SearchMigrationAdapter = (
  bffResults: HotelResultsViewModel,
  bexApiResults: OperationResult<PropertyMapMigration.Query>,
  bexApiPropertyDict: { [key: string]: PropertyMapMigration.Properties }
): HotelResultsViewModel => adaptPropertyField(bffResults, bexApiResults, getPrice, bexApiPropertyDict);

const getPrice = (property: PropertyMapMigration.Properties, bffHotel: HotelViewModel): { price: PriceViewModel } => ({
  price: property && property.price ? getPriceModel(property) : bffHotel.price
});

// Wave 4 is the infosite url
export const adaptPropertiesWave4: SearchMigrationAdapter = (
  bffResults: HotelResultsViewModel,
  bexApiResults: OperationResult<PropertyMapMigration.Query>,
  bexApiPropertyDict: { [key: string]: PropertyMapMigration.Properties }
): HotelResultsViewModel => adaptPropertyField(bffResults, bexApiResults, getInfositeUrl, bexApiPropertyDict);

const getInfositeUrl = (property: PropertyMapMigration.Properties, bffHotel: HotelViewModel): { infositeUrl: string } => ({
  infositeUrl: property && property.infositeURL ? property.infositeURL : bffHotel.infositeUrl
});

// Wave 5 is the shortlist model
export const adaptPropertiesWave5: SearchMigrationAdapter = (
  bffResults: HotelResultsViewModel,
  bexApiResults: OperationResult<PropertyMapMigration.Query>,
  bexApiPropertyDict: { [key: string]: PropertyMapMigration.Properties }
): HotelResultsViewModel => adaptPropertyField(bffResults, bexApiResults, getShortlistModel, bexApiPropertyDict);

const getShortlistModel = (
  property: PropertyMapMigration.Properties,
  bffHotel: HotelViewModel
): {
  shortlistModel: ShortlistModel;
} => ({
  shortlistModel: property && property.shortList ? getShortList(property) : bffHotel.shortlistModel
});

// Wave 6 is for PropertyListingBadge, PropertyFlags, RecentlyViewed model
export const adaptPropertiesWave6: SearchMigrationAdapter = (
  bffResults: HotelResultsViewModel,
  bexApiResults: OperationResult<PropertyMapMigration.Query>,
  bexApiPropertyDict: { [key: string]: PropertyMapMigration.Properties }
): HotelResultsViewModel => {
  const { data } = bexApiResults;
  const { propertySearch } = data;

  if (!propertySearch || !propertySearch.properties) {
    return bffResults;
  }

  const hotels = bffResults.hotels.map(hotel => {
    const bexApiHotel = bexApiPropertyDict[String(hotel.id)];

    return {
      ...hotel,
      ...getPropertyListingBadgeModel(bexApiHotel, hotel),
      ...getPropertyFlags(bexApiHotel, hotel),
      ...getRecentlyViewedModel(bexApiHotel, hotel)
    };
  });

  return {
    ...bffResults,
    hotels
  };
};

const getPropertyListingBadgeModel = (
  property: PropertyMapMigration.Properties,
  bffHotel: HotelViewModel
): {
  propertyListingBadgeModel: PropertyListingBadgeModel;
} => ({
  propertyListingBadgeModel:
    property && property.offerBadge ? BadgeAdapter.getPropertyListingBadgeModel(property) : bffHotel.propertyListingBadgeModel
});

const getPropertyFlags = (
  property: PropertyMapMigration.Properties,
  bffHotel: HotelViewModel
): {
  hotelFlags: HotelFlags;
} => ({
  hotelFlags: property && property.availability ? getHotelFlags(property) : bffHotel.hotelFlags
});

const getRecentlyViewedModel = (
  property: PropertyMapMigration.Properties,
  bffHotel: HotelViewModel
): {
  recentlyViewed: RecentlyViewedModel;
} => ({
  recentlyViewed: property && property.recentlyViewed ? getRecentlyViewed(property) : bffHotel.recentlyViewed
});

// Wave 7 is primaryCardMessages
export const adaptPropertiesWave7: SearchMigrationAdapter = (
  bffResults: HotelResultsViewModel,
  bexApiResults: OperationResult<PropertyMapMigration.Query>,
  bexApiPropertyDict: { [key: string]: PropertyMapMigration.Properties }
): HotelResultsViewModel => {
  const { data } = bexApiResults;
  const { propertySearch } = data;

  if (!propertySearch || !propertySearch.properties) {
    return bffResults;
  }

  const hotels = bffResults.hotels.map(hotel => {
    const bexApiHotel = bexApiPropertyDict[String(hotel.id)];

    return {
      ...hotel,
      ...adaptPrimaryCardMessages(bexApiHotel, hotel),
      ...adaptDisclaimerViewModel(bexApiHotel, hotel)
    };
  });

  return {
    ...bffResults,
    hotels
  };
};

const adaptPrimaryCardMessages = (
  property: PropertyMapMigration.Properties,
  bffHotel: HotelViewModel
): { primaryCardMessages: PrimaryCardMessagingModel[] } => ({
  primaryCardMessages:
    property && property.offerSummary && property.offerSummary.messages && property.offerSummary.messages.length > 0
      ? getPrimaryCardMessages(property)
      : bffHotel.primaryCardMessages
});

const adaptDisclaimerViewModel = (
  property: PropertyMapMigration.Properties,
  bffHotel: HotelViewModel
): { disclaimerDialog: DisclaimerViewModel } => ({
  disclaimerDialog: property && property.legalDisclaimer ? getDisclaimerViewModel(property) : bffHotel.disclaimerDialog
});

const adaptPropertyField = <T extends keyof HotelViewModel>(
  bffResults: HotelResultsViewModel,
  bexApiResults: OperationResult<PropertyMapMigration.Query>,
  fieldToMigrate: (bex: PropertyMapMigration.Properties, bff: HotelViewModel) => HotelViewModel[T],
  bexApiPropertyDict: { [key: string]: PropertyMapMigration.Properties }
): HotelResultsViewModel => {
  const { data } = bexApiResults;
  const { propertySearch } = data;

  if (!propertySearch || !propertySearch.properties) {
    return bffResults;
  }

  const hotels = bffResults.hotels.map(hotel => {
    const bexApiHotel = bexApiPropertyDict[String(hotel.id)];

    const field = fieldToMigrate(bexApiHotel, hotel);

    return {
      ...hotel,
      ...field
    };
  });

  return {
    ...bffResults,
    hotels
  };
};
